'use client';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuthUser } from '@/hooks/useAuthUser';
import { toVisibleGroups, useSidebarLayout } from '@/hooks/useSidebarLayout';
import { Link, usePathname } from '@/i18n/routing';
import { useAppDispatch } from '@/lib/redux/hooks';
import { setCustomizeSheetOpen } from '@/lib/redux/slices/uiSettingsSlice';
import { cn } from '@/lib/utils';
import { PanelRight, Sparkles, SquarePen } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { MouseEvent } from 'react';
import { SidebarBrand } from './SidebarBrand';
import {
  type SidebarSection,
  type SidebarSectionKey,
  getActiveSection,
} from './sidebarSections';

interface SidebarActionListProps {
  onNewChatClick: () => void;
  onSearchDocsClick?: (() => void) | undefined;
  onToggleCollapse?: (() => void) | undefined;
  showRightIcon?: boolean | undefined;
  // When the sidebar is collapsed, control whether to render the explicit
  // "open sidebar" button. Defaults to true to preserve existing behavior.
  showOpenButton?: boolean | undefined;
  // When true (mobile view), show a close (X) icon in brand
  useCloseIcon?: boolean | undefined;
  // Drop auth-only sections (Inbox) for anonymous users — same gate the
  // expanded SidebarRail applies (#6355).
  isAnonymousMode?: boolean | undefined;
}

// The nav rows are driven by the SAME source of truth + visibility gate as the
// primary rail (SidebarRail) — SIDEBAR_SECTIONS / visibleSidebarSections — so the
// collapsed rail and the expanded rail can never drift (issue #5384). The chat
// section is represented here by the bespoke "New chat" action (a chat-panel
// action, not a nav destination), so it is excluded from the data-driven list.
// Customize is the same shape — a bespoke action (opens CustomizeSheet via
// redux), not an href-based nav destination — so it is hand-added right after
// New chat rather than living in SIDEBAR_SECTIONS.

export const SidebarActionList = ({
  onNewChatClick,
  onSearchDocsClick,
  onToggleCollapse,
  showRightIcon = true,
  showOpenButton = true,
  useCloseIcon = false,
  isAnonymousMode = false,
}: SidebarActionListProps) => {
  const t = useTranslations('SidebarHeader');
  const tCommon = useTranslations('Common');
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const { practiceEnabled, planCapabilities, supabaseUser } = useAuthUser();
  // Same customized order/grouping as the expanded rail (useSidebarLayout is
  // the single source of truth so the two rails can't drift) — flattened
  // since the collapsed rail shows no group headers, just icons in order.
  // "chat" is the bespoke New-chat action, not a nav destination, so it's
  // excluded here regardless of which group the user has moved it into.
  const { groups } = useSidebarLayout({
    isAnonymousMode,
    practiceEnabled,
    capabilities: planCapabilities,
    // No loaded backend user → the plan can't be judged (#11227's gate rule).
    capabilitiesUnknown: supabaseUser == null,
  });
  const navSections = toVisibleGroups(groups)
    .flatMap((g) => g.items)
    .filter((s) => s.key !== 'chat');

  // Active state is resolved by the canonical getActiveSection (the same
  // descendant-matching logic this list used to hardcode). "New chat"
  // deliberately does NOT light up on `/chat/[sessionId]`: when a session is
  // open the SessionList highlights *that* session, so New Chat lights up only
  // on the new-chat landing (`/` or `/chat`).
  const activeSection = getActiveSection(pathname);
  const isActiveNewChat = pathname === '/' || pathname === '/chat';

  // Collapsed icon buttons keep a ≥44×44px interactive hit area (WCAG 2.1 AA
  // SC 2.5.5) via min-h/min-w; the icon glyph itself stays its visual size.
  const linkCollapsed = (active: boolean) =>
    cn(
      'group w-full flex items-center justify-center text-left rounded-md min-h-[44px] min-w-[44px] px-1.5 py-1.5 text-sm transition-colors cursor-pointer',
      active ? 'bg-interactive-hover' : 'hover:bg-interactive-hover'
    );

  const rowMargin = { marginLeft: showRightIcon ? '-4px' : '0px' };

  // Search-legislation keeps its callback hook (closes the drawer / resets) and
  // stops propagation so navigating doesn't also expand the collapsed rail
  // (the rail's parent onClick). Every other section just navigates via its
  // <Link> AND lets the click bubble to expand the rail — so they need no
  // onClick of their own (sectionClick returns undefined for them).
  const onSearchLegislationClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    onSearchDocsClick?.();
  };
  const sectionClick = (
    key: SidebarSectionKey
  ): ((e: MouseEvent) => void) | undefined =>
    key === 'searchLegislation' ? onSearchLegislationClick : undefined;

  const badgeText = (badge: SidebarSection['badge']) => {
    if (badge === 'beta') return tCommon('beta');
    if (badge === 'locked') return tCommon('locked');
    if (badge === 'comingSoon') return tCommon('comingSoon');
    return null;
  };
  // 'locked' (#7198) gets its own premium-toned dot so it reads distinctly
  // from 'beta'/'comingSoon' — the collapsed rail has no room for a lock
  // glyph, so colour + the tooltip's badge text carry the signal here. Uses
  // `premium`, not `status-warning` — see SidebarRail.tsx's badgePill for why
  // (an upsell affordance, not an alert).
  const badgeDotClass = (badge: SidebarSection['badge']) => {
    if (badge === 'beta') return 'bg-brand-tertiary';
    if (badge === 'locked') return 'bg-premium';
    return 'bg-status-info';
  };

  const renderCollapsedSection = (section: SidebarSection) => {
    const Icon = section.icon;
    const active = activeSection === section.key;
    const label = t(section.labelKey as Parameters<typeof t>[0]);
    const badge = badgeText(section.badge);

    if (section.disabled || !section.href) {
      // Disabled (e.g. "coming soon") — a focusable, aria-disabled button
      // rather than a bare div+aria-label (which screen readers do not
      // reliably expose). Mirrors SidebarRail's disabled-row idiom so the
      // collapsed and expanded rails announce the affordance identically
      // (#5159). The status is folded into the accessible name so SR users
      // hear "Generate document, coming soon", matching the visible tooltip.
      const accessibleName = badge ? `${label}, ${badge}` : label;
      return (
        <Tooltip key={section.key}>
          <TooltipTrigger asChild>
            <button
              type='button'
              aria-disabled='true'
              aria-label={accessibleName}
              // a11y (#6058): the tooltip conveys the same information as this
              // aria-label (the badge is folded into accessibleName, so the
              // tooltip's "label (badge)" duplicates the name's "label, badge"),
              // so suppress Radix's duplicate `aria-describedby` — it would make
              // a screen reader announce the name twice. Overriding the trigger
              // child's value wins via Radix Slot mergeProps and React drops the
              // attribute; the visible tooltip is unaffected.
              aria-describedby={undefined}
              onClick={(e) => e.preventDefault()}
              className='group w-full flex items-center justify-center text-left rounded-md min-h-[44px] min-w-[44px] px-1.5 py-1.5 text-sm cursor-default relative'
            >
              <Icon size={20} className='text-content-muted' />
              {section.badge && (
                <span
                  aria-hidden='true'
                  className={cn(
                    'absolute -top-0.5 -right-1 h-2 w-2 rounded-full',
                    badgeDotClass(section.badge)
                  )}
                />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side='right' align='center' sideOffset={8}>
            <span>
              {label}
              {badge ? ` (${badge})` : ''}
            </span>
          </TooltipContent>
        </Tooltip>
      );
    }

    // Fold any badge into the accessible name so SR users hear "Inbox, Beta"
    // (matching SidebarRail's renderSection and this file's disabled branch).
    // This makes the tooltip ("Inbox (Beta)") a true duplicate of the name —
    // so suppressing its aria-describedby below drops nothing SR-only (#6058):
    // before folding it in, the badge text lived ONLY in the tooltip span, and
    // suppression would have made the beta/coming-soon status inaudible.
    const accessibleName = badge ? `${label}, ${badge}` : label;
    return (
      <Tooltip key={section.key}>
        <TooltipTrigger asChild>
          <Link
            href={section.href as never}
            aria-label={accessibleName}
            // a11y (#6058): tooltip duplicates this aria-label — suppress the
            // redundant Radix `aria-describedby` so SR announces the name once.
            aria-describedby={undefined}
            onClick={sectionClick(section.key)}
            aria-current={active ? 'page' : undefined}
            className={cn(linkCollapsed(active), section.badge && 'relative')}
          >
            <Icon size={20} className='text-brand-primary-color' />
            {section.badge && (
              <span
                aria-hidden='true'
                className={cn(
                  'absolute -top-0.5 -right-1 h-2 w-2 rounded-full',
                  badgeDotClass(section.badge)
                )}
              />
            )}
          </Link>
        </TooltipTrigger>
        <TooltipContent side='right' align='center' sideOffset={8}>
          <span>
            {label}
            {badge ? ` (${badge})` : ''}
          </span>
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    // A <nav> landmark (not a bare div) so AT users can jump straight to the
    // app navigation — mirrors SidebarRail's landmark (#5454).
    <nav
      aria-label={tCommon('appNavigation')}
      // Collapsed rail: center the logo + every icon button on a single
      // vertical column so the 44px touch targets sit dead-centre in the
      // 56px rail (#5158). This list only ever renders in the collapsed rail
      // (post-#5105 two-column nav redesign), so the centering is unconditional.
      className='p-3 space-y-1.5 sticky top-0 z-10 flex flex-col items-center'
    >
      <div className='mb-4'>
        <SidebarBrand
          showText={false}
          onToggleCollapse={onToggleCollapse}
          showRightIcon={showRightIcon}
          // Collapsed rail (no right icon): show ONLY the expand button, no
          // Lexomat logo — the 56px rail is icon-only (#6212-follow-up).
          expandButtonOnly={!showRightIcon}
          useCloseIcon={useCloseIcon}
        />
      </div>

      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              href='/'
              aria-label={t('newChat')}
              // a11y (#6058): tooltip duplicates this aria-label — suppress the
              // redundant Radix `aria-describedby` so SR announces it once.
              aria-describedby={undefined}
              onClick={(e) => {
                // Only call onNewChatClick if not opening in new tab
                if (!e.metaKey && !e.ctrlKey && !e.shiftKey) {
                  e.stopPropagation();
                  onNewChatClick();
                }
              }}
              aria-current={isActiveNewChat ? 'page' : undefined}
              className={linkCollapsed(isActiveNewChat)}
            >
              <SquarePen size={20} className='text-brand-primary-color' />
            </Link>
          </TooltipTrigger>
          <TooltipContent side='right' align='center' sideOffset={8}>
            <span>{t('newChat')}</span>
          </TooltipContent>
        </Tooltip>

        {/* Customize — quick-access shortcut mirroring the one added to the
            expanded rail (SidebarRail), right below Chats. The full entry
            still lives in the footer menu (SidebarMenuContent); this opens
            the same CustomizeSheet via redux. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type='button'
              aria-label={t('customize')}
              aria-describedby={undefined}
              onClick={(e) => {
                e.stopPropagation();
                dispatch(setCustomizeSheetOpen(true));
              }}
              className={linkCollapsed(false)}
            >
              <Sparkles size={20} className='text-brand-primary-color' />
            </button>
          </TooltipTrigger>
          <TooltipContent side='right' align='center' sideOffset={8}>
            <span>{t('customize')}</span>
          </TooltipContent>
        </Tooltip>

        {navSections.map(renderCollapsedSection)}
      </TooltipProvider>

      {showOpenButton && !showRightIcon && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type='button'
                aria-label={tCommon('openSidebar')}
                // a11y (#6058): tooltip duplicates this aria-label — suppress
                // the redundant Radix `aria-describedby` (SR double-announce).
                aria-describedby={undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleCollapse?.();
                }}
                className='w-full flex items-center justify-center min-h-[44px] min-w-[44px] text-left rounded-md px-1.5 py-1.5 text-sm hover:bg-interactive-hover transition-colors cursor-pointer'
                style={rowMargin}
              >
                <PanelRight size={18} />
              </button>
            </TooltipTrigger>
            <TooltipContent side='right' align='center' sideOffset={8}>
              <span>{tCommon('openSidebar')}</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </nav>
  );
};
